import React, { useState, useRef } from "react"
import useMapResults from "./map-logic/useMapResults"
import { useSiteTranslations } from "@hooks/useSiteTranslations"
import ImporterMapPin from "@assets/icons/marker.png"
import DistributorMapPin from "@assets/icons/marker1.png"
import POSMapPin from "@assets/icons/marker2.png"

import { TextInput } from "@components/common/form/TextInput"
import { Cta } from "@components/common/cta/Cta"

import * as s from "./Locator.module.scss"

export const Locator = ({ className, data }) => {
  const [postcode, setPostcode] = useState("")
  const googleMapRef = useRef(null)
  const {
    currentLocation,
    mapResults,
    loading,
    postcodeError,
    fetchLocationData,
    resetMarkers,
  } = useMapResults(data.storeGroup, googleMapRef)

  const {
    generalTranslations: { general },
  } = useSiteTranslations()
  const onFormSubmit = () => fetchLocationData(postcode)

  const onPostcodeChange = event => {
    setPostcode(event.target.value)
    if (currentLocation) fetchLocationData(event.target.value)
  }

  const onPostcodeKeyDown = event => {
    if (event.key === "Enter") {
      event.preventDefault()
      event.stopPropagation()
      fetchLocationData(event.target.value)
    }
  }

  const hideInitialInfo = Boolean(currentLocation) || loading

  return (
    <div className={`${s.Locator} ${className || ""}`}>
      <div
        className={s.Locator__info}
        style={{ backgroundColor: data.bgColor || "#f02937" }}
      >
        <div
          className={`${s.Locator__infoInital} ${
            hideInitialInfo ? s.Locator__infoInital___hasLocation : ""
          }`}
        >
          {data?.iconImage?.sourceUrl && (
            <img
              src={data.iconImage.sourceUrl}
              alt={data.iconImage.altText || ""}
              className={`${s.Locator__infoInital__iconImage} ${
                hideInitialInfo
                  ? s.Locator__infoInital___hasLocation__iconImage
                  : ""
              }`}
            />
          )}
          <h4>{data.title}</h4>
          <p>{data.blurb}</p>

          {data.showMapKey && (
            <div className={s.Locator__key}>
              <div>
                <img src={ImporterMapPin} alt="Importer" />
                <span>Importer</span>
              </div>
              <div>
                <img
                  src={DistributorMapPin}
                  alt="Distributors and wholesalers"
                />
                <span>
                  Distributors and wholesalers of the on-trade/horeca channel
                  (bars, pubs, restaurants, etc.)
                </span>
              </div>
              <div>
                <img src={POSMapPin} alt="Points of sale" />
                <span>
                  Points of sale for private customers (supermarkets,
                  hypermarkets, grocery stores, etc.)
                </span>
              </div>
            </div>
          )}

          <TextInput
            placeholder={general.productLocator.postcodeInputPlaceholderText}
            value={postcode}
            className={s.Locator__input}
            onChange={onPostcodeChange}
            onKeyDown={onPostcodeKeyDown}
          />
          {postcodeError && <p>{general.productLocator.postcodeErrorText}</p>}
          <Cta
            className={`${s.Locator__infoInital__cta} ${
              hideInitialInfo ? s.Locator__infoInital___hasLocation__cta : ""
            }`}
            title={data.ctaText || "Find"}
            onClick={onFormSubmit}
          />
        </div>

        <div
          className={`${s.Locator__infoList} ${
            hideInitialInfo ? s.Locator__infoList___hasLocation : ""
          }`}
        >
          {loading ? (
            <div className={s.Locator__loading}>
              <p>{general.productLocator.locatorLoadingText}</p>
            </div>
          ) : mapResults.length < 1 ? (
            <div className={s.Locator__loading}>
              <p>{general.productLocator.locatorLoadingNoResultsText}</p>
            </div>
          ) : (
            <>
              {mapResults.map((item, i) => {
                const { acf, title, distance } = item
                const { street_number, street_name, city, postal_code } =
                  acf?.location || {}

                return (
                  <div
                    key={i}
                    className={s.Locator__location}
                    onClick={() => {
                      resetMarkers()

                      item.onClick()
                    }}
                  >
                    <span
                      className={s.Locator__location__title}
                      dangerouslySetInnerHTML={{ __html: title.rendered }}
                    />

                    <span className={s.Locator__location__address}>
                      {street_number && `${street_number} `}
                      {street_name && `${street_name}, `}
                      {city && `${city}, `}
                      {postal_code && postal_code}
                    </span>

                    {acf?.website && (
                      <div className={s.Locator__location__meta}>
                        <span>Website:</span>
                        <a href={acf.website} target="_blank" rel="noreferrer">
                          {acf.website}
                        </a>
                      </div>
                    )}

                    {acf?.email && (
                      <div className={s.Locator__location__meta}>
                        <span>Email:</span>
                        <a href={acf.email} target="_blank" rel="noreferrer">
                          {acf.email}
                        </a>
                      </div>
                    )}

                    <Cta
                      title={`${distance} ${general.productLocator.locationCtaText}`}
                      target="_blank"
                      external={true}
                      link={`https://maps.google.com/?q=${street_number} ${street_name} ${city} ${postal_code}`}
                    />
                  </div>
                )
              })}
            </>
          )}
        </div>
      </div>
      <div className={s.Locator__map}>
        <div id="google-map" ref={googleMapRef} />
      </div>
    </div>
  )
}
